/**
 * Article rédigé le 2026-09-06 à partir de sources web ouvertes et vérifiées automatiquement
 * (6 URL testées, révisé après vérification). Daté au moment des faits : 2025-08-28.
 * À relire avant/après mise en ligne. Format : voir src/blog/README.md
 */
export default {
  "slug": "premiere-pierre-le-numerique-planoise-besancon",
  "title": "« Le Numérique » : première pierre du lieu totem de la tech bisontine",
  "excerpt": "Aux 9-11 rue du Luxembourg, à l'entrée de Planoise, la première pierre du « Numérique » a été posée le 26 août 2025 : un tiers-lieu de 3 000 m² à 10,5 M€, attendu mi-2027.",
  "seoTitle": "Le Numérique : première pierre à Planoise",
  "seoDescription": "Première pierre posée le 26 août 2025 rue du Luxembourg : 3 000 m², 10,5 M€ HT et une ouverture visée mi-2027 pour le futur tiers-lieu numérique de Planoise.",
  "category": "collectivites",
  "tags": [
    "Le Numérique",
    "Planoise",
    "Grand Besançon Métropole",
    "French Tech",
    "Tiers-lieu"
  ],
  "author": "jimmy",
  "publishedAt": "2025-08-28",
  "updatedAt": "2025-08-28",
  "featured": false,
  "cover": {
    "image": "/img/blog/besancon-vue-generale-du-quartier-de-planoise.jpg",
    "credit": "Photo : Besançon, vue générale du quartier de Planoise — Wikipedro, CC BY-SA 4.0, via Wikimedia Commons",
    "alt": "Vue générale du quartier de Planoise à Besançon depuis la colline de Rosemont : barres d'immeubles blanches, grues de chantier et collines boisées à l'horizon"
  },
  "content": [
    {
      "type": "lead",
      "text": "Le 26 août 2025, Grand Besançon Métropole et ses partenaires ont posé la première pierre du « Numérique », aux 9-11 rue du Luxembourg, à l'entrée du quartier de Planoise. Trois mille mètres carrés, 10,5 millions d'euros hors taxes, une ouverture visée mi-2027 : le futur bâtiment doit devenir le point d'ancrage physique de la filière numérique du Grand Besançon. Pour l'instant, le terrain est nu."
    },
    {
      "type": "keyfacts",
      "title": "En bref",
      "facts": [
        {
          "label": "Quoi",
          "value": "« Le Numérique », tiers-lieu de 3 000 m² dédié à la filière numérique, 10,5 M€ HT"
        },
        {
          "label": "Quand",
          "value": "Première pierre le 26 août 2025, chantier à partir de novembre 2025, ouverture visée mi-2027"
        },
        {
          "label": "Où",
          "value": "9-11 rue du Luxembourg, quartier de Planoise, Besançon"
        },
        {
          "label": "Qui",
          "value": "Grand Besançon Métropole et sa SPL Territoire 25, avec l'ANRU, la Région Bourgogne-Franche-Comté et la Banque des Territoires"
        },
        {
          "label": "Pour qui",
          "value": "Habitants, TPE-PME en transition numérique, startups, acteurs de la formation"
        }
      ]
    },
    {
      "type": "h2",
      "text": "Une parcelle vide depuis 2022"
    },
    {
      "type": "p",
      "text": "L'adresse ne dit rien à qui ne passe pas par Planoise, mais elle a une histoire courte et parlante. Aux 9-11 rue du Luxembourg se dressaient les immeubles des Charmettes[^4], démolis entre mars et novembre 2022 dans le cadre du Nouveau programme national de renouvellement urbain (NPNRU) du quartier[^1]. Depuis, l'espace est resté vide, le sol simplement terrassé[^2]."
    },
    {
      "type": "p",
      "text": "C'est sur cette parcelle que Grand Besançon Métropole et sa société publique locale Territoire 25 ont posé la première pierre du bâtiment le 26 août[^4], jour de la conférence de presse de présentation du projet[^2]. Le chantier proprement dit démarrera en novembre 2025, pour une livraison annoncée à mi-2027[^1]. La logique du NPNRU est ici assumée : ne pas seulement reconstruire du logement, mais implanter un équipement économique dans un quartier prioritaire. Planoise est d'ailleurs désigné « quartier d'excellence numérique » dans le dispositif ANRU+[^1]."
    },
    {
      "type": "h2",
      "text": "Un rez-de-chaussée public, des étages pour les entreprises"
    },
    {
      "type": "p",
      "text": "Le programme — 3 000 m² selon Grand Besançon Métropole[^1], environ 3 072 m² répartis sur cinq étages selon la French Tech Bourgogne-Franche-Comté[^3] — repose sur une séparation nette entre le bas et le haut du bâtiment. Le rez-de-chaussée, vitré, accueillera un tiers-lieu ouvert au public : accueil, information et formation au numérique, avec un rôle explicite d'aide aux démarches administratives en ligne[^2]. Au-dessus, sur des niveaux de bureaux, se déploieront le coworking en contrats d'occupation flexibles et des bureaux ou open-spaces en location[^2]."
    },
    {
      "type": "p",
      "text": "Dans le détail, le lieu doit réunir :"
    },
    {
      "type": "ul",
      "items": [
        "un **FabLab public**[^3] équipé de machines numériques, accessible ponctuellement ou via un abonnement[^2] ;",
        "une **salle immersive à 360°**, pensée pour la formation, la démonstration et l'acculturation[^3] ;",
        "des **espaces de formation** aux usages et aux métiers du numérique[^1] ;",
        "des **espaces résidents** pour les entreprises du numérique et l'accueil d'équipes R&D[^1] ;",
        "une **salle des hôtes** pour des animations[^2]."
      ]
    },
    {
      "type": "p",
      "text": "Le bâtiment lui-même est présenté comme un démonstrateur : structure mixte bois-béton, photovoltaïque en toiture, récupération d'eau de pluie, raccordement au réseau de chaleur urbain et gestion technique du bâtiment (GTB)[^3]. La construction vise le label R2S (Ready2Services)[^3], qui répond aux quatre principaux enjeux d'un bâtiment « connecté et sobre » : économies d'énergie, service, confort, sécurité[^1]. Le projet a été conçu par l'architecte Jean-Baptiste Barbet[^2]."
    },
    {
      "type": "h2",
      "text": "Un plan de financement à cinq entrées"
    },
    {
      "type": "p",
      "text": "Les 10,5 M€ HT reposent sur un empilement de concours publics — quatre subventions et un prêt —, caractéristique des opérations de renouvellement urbain. Les cinq lignes rendues publiques, additionnées, représentent 9,8 M€[^2] :"
    },
    {
      "type": "ul",
      "items": [
        "ANRU, au titre de la convention pluriannuelle NPNRU : **1,5 M€** ;",
        "ANRU+, volet « Planoise – quartier d'excellence du numérique » : **1,2 M€** ;",
        "Région Bourgogne-Franche-Comté : **2,8 M€** ;",
        "Grand Besançon Métropole, participation à l'investissement : **2 M€** ;",
        "Banque des Territoires, prêt renouvellement urbain : **2,3 M€**."
      ]
    },
    {
      "type": "p",
      "text": "Ces cinq lignes n'atteignent pas le coût annoncé de 10,5 M€ ; le détail du solde ne figure pas dans les éléments repris par la presse locale[^2]. Le prêt renouvellement urbain de la [Banque des Territoires](https://www.banquedesterritoires.fr/pose-de-premiere-pierre-le-numerique-besancon) est, lui, garanti à la fois par Grand Besançon Métropole et par la Commission européenne, via ce que Grand Besançon Métropole présente comme le « nouveau dispositif Invest EU »[^1]. Autrement dit, un mécanisme de garantie européen vient sécuriser un chantier de quartier bisontin."
    },
    {
      "type": "h2",
      "text": "Le « lieu totem » revendiqué par la métropole"
    },
    {
      "type": "p",
      "text": "L'intérêt du projet dépasse le bâti. Grand Besançon Métropole annonce clairement que le lieu a vocation à devenir « le lieu totem » d'une des filières d'excellence du territoire et de la French Tech[^1]. [La French Tech Bourgogne-Franche-Comté](/blog/french-tech-bfc-capitale-2026-2028-besancon) indique de son côté qu'elle travaillera avec la métropole, Territoire 25 et les partenaires pour préparer l'animation, les usages et les parcours entrepreneurs du futur site[^3]."
    },
    {
      "type": "p",
      "text": "Reste la question de l'occupation. L'écosystème numérique bisontin dispose déjà de briques éparses : le FabLab des Fabriques, géré par Grand Besançon Métropole, fonctionne au 10 rue Picasso, avec imprimantes 3D, découpe laser, fraiseuse numérique et matériel électronique — Arduino, Raspberry Pi, robotique[^5]. À la même adresse, l'association [Fabrique Numérique Besançon](/blog/fete-innovation-2026-french-tech-besancon), déclarée le 24 novembre 2020, s'est donné pour objet de rassembler acteurs publics et privés du territoire autour de l'innovation, de la formation, de l'insertion et du développement économique[^6]. Les documents publics ne précisent pas, à ce stade, quelles structures s'installeront dans les murs du Numérique ni sous quel modèle de gestion — c'est probablement le prochain sujet à suivre."
    },
    {
      "type": "callout",
      "tone": "tip",
      "title": "Pourquoi ça compte pour Besançon",
      "text": "Besançon compte déjà des acteurs et des lieux du numérique, mais dispersés dans la ville : un FabLab ici, une association là. C'est Grand Besançon Métropole qui revendique pour Le Numérique le statut de « lieu totem » de la filière[^1]. Le bâtiment propose de réunir sous un même toit l'inclusion numérique des habitants, la formation et l'hébergement d'entreprises. C'est aussi un pari d'aménagement : implanter cette adresse à Planoise plutôt qu'en centre-ville ou sur un parc technologique."
    },
    {
      "type": "h2",
      "text": "Deux ans d'attente"
    },
    {
      "type": "p",
      "text": "D'ici mi-2027, l'essentiel se jouera hors du chantier : dans la définition des services, la tarification du FabLab, les partenariats de formation et la capacité à faire venir à Planoise un public qui n'y va pas spontanément. Un bâtiment ne crée pas un écosystème. Il lui donne, au mieux, un lieu où se croiser. [Nous suivrons le chantier](/blog/le-numerique-planoise-visite-chantier-2026) et la préfiguration des usages au fil des prochains mois."
    }
  ],
  "references": [
    {
      "id": 1,
      "title": "Un lieu totem du numérique en construction à Planoise",
      "publisher": "Grand Besançon Métropole",
      "url": "https://www.grandbesancon.fr/un-lieu-totem-du-numerique-en-construction-a-planoise/",
      "type": "official",
      "date": "2023-11-16"
    },
    {
      "id": 2,
      "title": "« Le Numérique » : un bâtiment de 3.000 m2 à 10,5 M€ bientôt au cœur de Planoise",
      "publisher": "macommune.info",
      "url": "https://www.macommune.info/le-numerique-un-batiment-de-3-000-m2-a-105-me-bientot-au-coeur-de-planoise/",
      "type": "press",
      "date": "2025-08-27"
    },
    {
      "id": 3,
      "title": "À Planoise, la première pierre du futur lieu totem numérique de Besançon est posée",
      "publisher": "La French Tech Bourgogne-Franche-Comté",
      "url": "https://lafrenchtechbfc.fr/actualites/ecosysteme/a-planoise-la-premiere-pierre-du-futur-lieu-totem-numerique-de-besancon-est-posee/",
      "type": "web",
      "date": "2025-08-27"
    },
    {
      "id": 4,
      "title": "Pose de première pierre « Le Numérique » à Besançon",
      "publisher": "Banque des Territoires",
      "url": "https://www.banquedesterritoires.fr/pose-de-premiere-pierre-le-numerique-besancon",
      "type": "official",
      "date": "2025-08-26"
    },
    {
      "id": 5,
      "title": "FabLab des Fabriques du Grand Besançon",
      "publisher": "Grand Besançon Métropole",
      "url": "https://www.grandbesancon.fr/infos-pratiques/sports-loisirs/numerique/fablab-du-grand-besancon/",
      "type": "official",
      "date": "2019-07-16"
    },
    {
      "id": 6,
      "title": "Association Fabrique Numérique Besançon (RNA W251009532)",
      "publisher": "Net1901 — Journal Officiel des associations",
      "url": "https://www.net1901.org/association/FABRIQUE-NUMERIQUE-BESANCON,1000049418.html",
      "type": "web",
      "date": "2020-12-01"
    }
  ]
};
